import splitAtDelimitersModule from "katex/contrib/auto-render/splitAtDelimiters.js";
import "katex/dist/contrib/mhchem.mjs";
import katex from "katex/dist/katex.mjs";

import { MATH_DELIMITERS } from "@/lib/math-delimiters";
import { KATEX_MACROS } from "@/lib/katex-macros.mjs";
import { isTrustedKatexCommand } from "@/lib/katex-trust.mjs";
import { stripMathEscapes } from "@/lib/strip-math-escapes.mjs";

export type MathSegment =
  | { type: "text"; data: string }
  | {
      type: "math";
      data: string;
      rawData?: string;
      display: boolean;
    };

const KATEX_BASE_OPTIONS = {
  throwOnError: false,
  macros: KATEX_MACROS,
  trust: isTrustedKatexCommand,
};

export function normaliseMathText(value: string): string {
  return stripMathEscapes(value)
    .replace(/\\\\/g, "\\")
    .replace(/\\\{/g, "{")
    .replace(/\\\}/g, "}")
    .replace(/\\_/g, "_");
}

// Like normaliseMathText, but skips stripMathEscapes. Use this on values that
// are already known to be math bodies (e.g. the value of a remark `math` /
// `inlineMath` node, or a LaTeX string handed to a planner). stripMathEscapes
// is built for prose: its (x = ...) → \( x = ... \) heuristic is correct
// outside math delimiters but corrupts valid LaTeX once we are inside one.
export function normaliseMathBody(value: string): string {
  return value
    .replace(/\\\\/g, "\\")
    .replace(/\\\{/g, "{")
    .replace(/\\\}/g, "}")
    .replace(/\\_/g, "_");
}

export function normalisePlainText(value: string): string {
  return value.replace(/\u2014/g, "-");
}

const splitAtDelimiters =
  typeof splitAtDelimitersModule === "function"
    ? splitAtDelimitersModule
    : (splitAtDelimitersModule as { default: typeof splitAtDelimitersModule })
        .default;

type RawSegment = ReturnType<typeof splitAtDelimiters>[number];

function toMathSegment(segment: RawSegment): MathSegment {
  if (segment.type === "text") {
    return { type: "text", data: segment.data };
  }

  return {
    type: "math",
    // Tina's rich-text parser consumes the source escape before literal
    // percentage and currency signs. Both characters must be escaped again
    // before the delimited body reaches KaTeX.
    data: segment.data.replace(/(?<!\\)([%$])/g, "\\$1"),
    rawData: segment.rawData,
    display: segment.display,
  };
}

export function splitMathSegments(value: string): MathSegment[] {
  const initial = splitAtDelimiters(
    normaliseMathText(value),
    MATH_DELIMITERS
  ).map(toMathSegment);

  if (initial.some((segment) => segment.type === "math")) {
    return initial;
  }

  if (!/\\[\[\]\(\)]/.test(value)) {
    return initial;
  }

  const doubled = value.replace(/\\([\[\]\(\)])/g, "\\\\$1");

  if (doubled === value) {
    return initial;
  }

  return splitAtDelimiters(normaliseMathText(doubled), MATH_DELIMITERS).map(
    toMathSegment
  );
}

export function renderMathToHtml(
  value: string,
  display: boolean | undefined,
  output: "htmlAndMathml" | "html" = "htmlAndMathml"
): string {
  return katex.renderToString(value, {
    ...KATEX_BASE_OPTIONS,
    displayMode: display ?? false,
    output,
  });
}

export function wrapMathHtml(html: string, display: boolean): string {
  const modifier = display ? "display" : "inline";
  return `<span class="math-render math-render--${modifier}">${html}</span>`;
}

export function renderContainedMathToHtml(
  value: string,
  display: boolean,
  output: "htmlAndMathml" | "html" = "htmlAndMathml"
): string {
  return wrapMathHtml(renderMathToHtml(value, display, output), display);
}
